/**
 * Org Detail page (Task 53c.2).
 *
 * URL: /orgs/:id with tab fragment as the last path segment:
 *   /orgs/:id              → overview (default)
 *   /orgs/:id/projects     → projects tab
 *   /orgs/:id/users        → users tab
 *   /orgs/:id/streaming    → streaming servers tab (Task 32c.2)
 *   /orgs/:id/billing      → plan & billing tab
 *   /orgs/:id/activity     → activity tab
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { TabBar, type TabItem, ErrorState, LoadingSkeleton } from '@repro/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { api } from '../../utils/api';
import type { Organisation } from '../../types';

const TABS: TabItem[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'projects', label: 'Projects' },
  { id: 'users', label: 'Users' },
  { id: 'streaming', label: 'Streaming' },
  { id: 'billing', label: 'Plan & Billing' },
  { id: 'activity', label: 'Activity' },
];

interface OrgDetailContext {
  organisation: Organisation;
}

export function OrgDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Organisation ID missing from URL');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getOrganisation(id)
      .then((org) => {
        if (!cancelled) setOrganisation(org);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load organisation');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Active tab is the last path segment, defaulting to 'overview'.
  const segments = location.pathname.replace(/\/$/, '').split('/');
  const last = segments[segments.length - 1];
  const activeId = TABS.some((t) => t.id === last) ? last : 'overview';

  const handleTabChange = (tabId: string) => {
    if (!id) return;
    if (tabId === 'overview') {
      navigate(`/orgs/${id}`);
    } else {
      navigate(`/orgs/${id}/${tabId}`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (error || !organisation) {
    return (
      <ErrorState
        title="Couldn't load organisation"
        body={error ?? 'Unknown error'}
        onRetry={() => navigate(0)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb / back link */}
      <div className="flex items-center gap-2 text-sm text-base-content/70">
        <button
          type="button"
          onClick={() => navigate('/orgs')}
          className="flex items-center gap-1 hover:text-base-content"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Organisations</span>
        </button>
        <span aria-hidden="true">/</span>
        <span className="text-base-content font-medium">{organisation.name}</span>
      </div>

      {/* Header */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-base-content">{organisation.name}</h1>
            <p className="text-sm text-base-content/70 mt-1">{organisation.billingEmail}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                organisation.enabled
                  ? 'bg-green-100 text-green-800'
                  : 'bg-base-200 text-base-content/70'
              }`}
            >
              {organisation.enabled ? 'Enabled' : 'Disabled'}
            </span>
            {organisation.planStatus && (
              <span className="text-xs text-base-content/60">
                Plan status: {organisation.planStatus}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <TabBar
        tabs={TABS}
        activeId={activeId}
        onChange={handleTabChange}
        ariaLabel="Organisation sections"
      />

      {/* Tab content rendered via nested route Outlet */}
      <div role="tabpanel" id={`tabpanel-${activeId}`} aria-labelledby={`tab-${activeId}`}>
        <Outlet context={{ organisation } satisfies OrgDetailContext} />
      </div>
    </div>
  );
}

export type { OrgDetailContext };
